import type { Profile } from "./questions";

export interface ProfileData {
  id: Profile;
  name: string;
  emoji: string;
  tagline: string;
  description: string;
  traits: string[];
  conseils: string[];
  punchline: string;
  nextStep: string;
  cta: string;
}

export const profiles: Record<Profile, ProfileData> = {
  visionnaire: {
    id: "visionnaire",
    name: "Visionnaire",
    emoji: "🔭",
    tagline: "Tu vois plus loin que les autres — et ça change tout.",
    description: `Tu vois plus loin que les autres… et tu le sais. Quand tu reçois de l'argent, ton cerveau lance direct un film 🎬 : "dans 3 ans, je veux être à tel niveau."

Tu n'investis pas pour "faire comme tout le monde" : tu investis pour construire un futur qui te ressemble. Les décisions impulsives ? Très peu pour toi. Toi, tu préfères un plan, une direction, une progression.

Ton truc, c'est la stratégie long terme, les moves qui paraissent petits aujourd'hui mais qui font très mal demain (dans le bon sens 😄). Et quand tu sens que ça peut scaler, tu passes la vitesse supérieure sans trembler 🔥.

⚡ Ton mantra : "Je construis maintenant ce que les autres voudront plus tard."`,
    traits: [
      "Tu anticipes les tendances avant qu'elles explosent",
      "Tu penses rendement long terme, pas gains rapides",
      "Tu fais confiance aux données, pas au bruit",
    ],
    conseils: [
      "Diversifie tes placements immobiliers pour maximiser ton potentiel",
      "Automatise tes investissements mensuels pour faire travailler le temps",
    ],
    punchline: "Je suis Visionnaire 🔭 — et toi ?",
    nextStep: "Commence à investir dès 1€ sur Ommistack et construis ton patrimoine du futur.",
    cta: "Découvrir Ommistack →",
  },
  chill: {
    id: "chill",
    name: "Chill",
    emoji: "😎",
    tagline: "Tranquille mais malin — tu investis sans prise de tête.",
    description: `Toi, tu veux que ton argent bosse… mais sans te voler ta paix mentale 😌.

Tu n'es pas là pour te prendre la tête avec 15 tableaux Excel, des prédictions ou des montagnes russes émotionnelles. Ton style : simple, régulier, clean. Tu avances tranquillement, et c'est ça qui te rend dangereux : tu tiens dans le temps 💪.

Tu as souvent ce réflexe "je sécurise d'abord", puis tu te fais plaisir après (ou l'inverse, mais toujours avec équilibre).

Et au fond, tu le sais : ton superpouvoir c'est pas l'excitation… c'est la constance. Tu construis ta liberté sans bruit 🤫.

⚡ Ton mantra : "Je fais simple. Je fais mieux. Je continue."`,
    traits: [
      "Tu veux que ça soit simple et sans stress",
      "Tu préfères les petits pas réguliers aux gros coups",
      "Tu profites de la vie ET tu prépares l'avenir",
    ],
    conseils: [
      "Mets en place un virement auto de 10€/mois — c'est déjà investir !",
      "Explore les projets immobiliers accessibles dès 1€ sur Ommistack",
    ],
    punchline: "Je suis Chill 😎 — et toi ?",
    nextStep: "Ommistack rend l'investissement aussi simple qu'un like.",
    cta: "Investir tranquille →",
  },
  stratege: {
    id: "stratege",
    name: "Stratège",
    emoji: "♟️",
    tagline: "Chaque euro a un plan — tu joues pour gagner.",
    description: `Toi, t'es pas là pour "tenter". T'es là pour optimiser 🎯.

Chaque euro a une mission. Tu réfléchis en scénarios, en options, en meilleurs coups possibles. Tu peux être détendu en apparence, mais dans ta tête ça calcule propre : risque, rendement, timing, horizon 🧠.

Tu détestes l'argent qui dort. Tu veux comprendre, maîtriser, et surtout éviter les erreurs bêtes. Tu écoutes peu les autres, sauf si la source est crédible — sinon, next.

Et quand tu investis, c'est rarement un coup de tête : c'est un choix assumé. Tu joues la partie, pas le tour ♟️.

⚡ Ton mantra : "Je ne cherche pas la chance. Je crée l'avantage."`,
    traits: [
      "Tu analyses avant d'agir",
      "Tu optimises tes placements avec méthode",
      "Tu sais que la patience est un super-pouvoir financier",
    ],
    conseils: [
      "Compare les rendements des différents projets avant d'investir",
      "Définis un objectif chiffré et suis ta progression chaque mois",
    ],
    punchline: "Je suis Stratège ♟️ — et toi ?",
    nextStep: "Ommistack te donne les outils pour suivre et optimiser chaque investissement.",
    cta: "Voir les projets →",
  },
  ambitieux: {
    id: "ambitieux",
    name: "Ambitieux",
    emoji: "🚀",
    tagline: "Tu vises la lune — et franchement, pourquoi pas ?",
    description: `Toi, tu veux du résultat. Et vite 💥.

T'as cette énergie "rien à perdre, tout à gagner" qui te pousse à agir quand d'autres hésitent. Quand tu gagnes de l'argent, tu penses direct en levier : "comment je le fais grossir ?"

T'es capable de prendre des risques, parce que tu vises une transformation, pas une petite amélioration. Le confort ? Oui, mais plus tard. Là, tu veux accélérer 🚀.

Ton moteur, c'est la liberté financière, la fierté, le niveau au-dessus. Et franchement… tu n'es pas fait pour rester dans la moyenne 🏆.

⚡ Ton mantra : "Je ne viens pas participer. Je viens gagner."`,
    traits: [
      "Tu veux construire quelque chose de grand",
      "Tu n'as pas peur de prendre des risques calculés",
      "Tu es motivé par la liberté financière",
    ],
    conseils: [
      "Augmente progressivement tes montants investis chaque trimestre",
      "Explore les projets à haut rendement sur Ommistack",
    ],
    punchline: "Je suis Ambitieux 🚀 — et toi ?",
    nextStep: "Lance-toi sur Ommistack et accélère ta montée en puissance.",
    cta: "Commencer maintenant →",
  },
  securitaire: {
    id: "securitaire",
    name: "Sécuritaire",
    emoji: "🛡️",
    tagline: "La prudence est ta force — tu construis sur du solide.",
    description: `Tu veux investir, oui. Mais pas au prix de ton sommeil 😴.

Ton cerveau repère tout de suite ce qui peut déraper : les promesses floues, les plans trop beaux, les gens trop sûrs d'eux. Tu veux du concret, du stable, du compréhensible.

Souvent, tu préfères avancer petit mais sûr. Et c'est pas de la peur : c'est de l'intelligence 🧠. Tu protèges ton futur, ton équilibre, tes proches.

Ton investissement idéal, c'est celui qui te rassure et te donne le sentiment de construire sans stress. Tu n'aimes pas les surprises (sauf les bonnes 🙃).

⚡ Ton mantra : "Je sécurise aujourd'hui pour être libre demain."`,
    traits: [
      "Tu protèges ton capital avant tout",
      "Tu choisis la stabilité sur le long terme",
      "Tu veux dormir tranquille avec tes placements",
    ],
    conseils: [
      "Commence petit et augmente quand tu es à l'aise",
      "Privilégie les projets stables avec des actifs immobiliers solides",
    ],
    punchline: "Je suis Sécuritaire 🛡️ — et toi ?",
    nextStep: "Ommistack te permet d'investir dans des projets immobiliers réels et rentables.",
    cta: "En savoir plus →",
  },
};
